import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

function minutesSince(value) {
  if (!value) return 0;
  const then = new Date(value).getTime();
  if (!then) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}

function ageLabel(value, t) {
  const mins = minutesSince(value);
  if (mins < 60) return `${mins} ${t('min')}`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return `${hours} ${t('h')} ${rest} ${t('min')}`;
}

function dueLabel(value, t) {
  if (!value) return '-';
  const due = new Date(value).getTime();
  if (!due) return '-';
  const diff = Math.floor((due - Date.now()) / 60000);
  if (diff < 0) return `${t('Late by')} ${Math.abs(diff)} ${t('min')}`;
  if (diff < 60) return `${t('In')} ${diff} ${t('min')}`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `${t('In')} ${h} ${t('h')} ${m} ${t('min')}`;
}

export default function DispatchBoard() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await API.get('orders/').catch(() => ({ data: [] }));
    setOrders(Array.isArray(res.data) ? res.data : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const board = useMemo(() => {
    const pending = orders.filter((o) => o.status === 'pending');
    const offered = orders.filter((o) => o.status === 'offered');
    const notAccepted = orders.filter((o) => {
      const assignments = Array.isArray(o.assignments) ? o.assignments : [];
      const hasAccepted = assignments.some((a) => a.status === 'accepted');
      const hasRejected = assignments.some((a) => a.status === 'rejected');
      return !hasAccepted && hasRejected && ['pending', 'offered'].includes(o.status);
    });

    const activeQueue = orders.filter((o) => ['pending', 'offered', 'assigned', 'picked_up', 'in_transit'].includes(o.status));
    const scheduled = activeQueue.filter((o) => Boolean(o.scheduled_delivery));
    const scheduledSoon = scheduled.filter((o) => {
      const due = new Date(o.scheduled_delivery).getTime();
      const diffMins = (due - Date.now()) / 60000;
      return diffMins >= 0 && diffMins <= 120;
    });
    const scheduledLate = scheduled.filter((o) => {
      const due = new Date(o.scheduled_delivery).getTime();
      return due < Date.now();
    });

    const avgOfferAge = offered.length
      ? Math.round(offered.reduce((acc, item) => acc + minutesSince(item.updated_at || item.created_at), 0) / offered.length)
      : 0;

    return {
      pending,
      offered,
      notAccepted,
      scheduledSoon,
      scheduledLate,
      stats: {
        totalQueue: activeQueue.length,
        pending: pending.length,
        offered: offered.length,
        notAccepted: notAccepted.length,
        scheduledSoon: scheduledSoon.length,
        scheduledLate: scheduledLate.length,
        avgOfferAge,
      },
    };
  }, [orders]);

  const statusLabel = (status) => {
    const mapped = t(`status.${status}`);
    return mapped === `status.${status}` ? status : mapped;
  };

  const Section = ({ title, rows, emptyText }) => (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="row-between" style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <span className="badge info">{rows.length}</span>
      </div>
      {rows.length === 0 ? <div className="mini-meta">{emptyText}</div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Order Number')}</th>
                <th>{t('Status')}</th>
                <th>{t('Driver')}</th>
                <th>{t('Created At')}</th>
                <th>{t('Age')}</th>
                <th>{t('Scheduled Delivery')}</th>
                <th>{t('Time To Due')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id}>
                  <td>{o.order_number}</td>
                  <td><span className={`badge ${o.status}`}>{statusLabel(o.status)}</span></td>
                  <td>{o.driver_name || '-'}</td>
                  <td>{o.created_at ? new Date(o.created_at).toLocaleString() : '-'}</td>
                  <td>{ageLabel(o.updated_at || o.created_at, t)}</td>
                  <td>{o.scheduled_delivery ? new Date(o.scheduled_delivery).toLocaleString() : '-'}</td>
                  <td>{dueLabel(o.scheduled_delivery, t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <Layout title={t('Smart Dispatch Board')}>
      <div className="stats-grid">
        <div className="stat-card"><h4>{t('Queue Total')}</h4><p>{board.stats.totalQueue}</p></div>
        <div className="stat-card warning"><h4>{t('Pending')}</h4><p>{board.stats.pending}</p></div>
        <div className="stat-card info"><h4>{t('Offered')}</h4><p>{board.stats.offered}</p></div>
        <div className="stat-card"><h4>{t('Not Accepted')}</h4><p>{board.stats.notAccepted}</p></div>
        <div className="stat-card success"><h4>{t('Scheduled Soon')}</h4><p>{board.stats.scheduledSoon}</p></div>
        <div className="stat-card" style={{ borderColor: '#ef4444' }}><h4>{t('Scheduled Late')}</h4><p style={{ color: '#f87171' }}>{board.stats.scheduledLate}</p></div>
        <div className="stat-card"><h4>{t('Avg Offer Wait')}</h4><p>{board.stats.avgOfferAge} {t('min')}</p></div>
      </div>

      {loading && <div className="card" style={{ marginBottom: 12 }}>{t('Loading...')}</div>}

      <Section title={t('Pending Queue')} rows={board.pending} emptyText={t('No pending orders')} />
      <Section title={t('Offered Queue')} rows={board.offered} emptyText={t('No offered orders')} />
      <Section title={t('Not Accepted Queue')} rows={board.notAccepted} emptyText={t('No not-accepted orders')} />

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ marginTop: 0 }}>{t('Alerts')}</h3>
        <div className="mini-meta">{t('Scheduled alerts and queue risks update every 30 seconds.')}</div>
        <ul style={{ marginBottom: 0 }}>
          <li>{t('Scheduled Soon')}: {board.stats.scheduledSoon}</li>
          <li>{t('Scheduled Late')}: {board.stats.scheduledLate}</li>
          <li>{t('Not Accepted')}: {board.stats.notAccepted}</li>
        </ul>
      </div>
    </Layout>
  );
}
